import type { DeviceMediaItem } from "@workspace/api-client-react";
import { t } from "@workspace/strings/common";

import type { QueueEntry } from "./playback-engine.ts";

/**
 * Pre-announcement attention tone policy — shared by every player surface.
 *
 * A short chime played once at the head of an announcement takeover
 * (scheduled or live), configured per device via `settings.announcementTone`.
 * Tone entries ride the normal interrupt queue and advance on ended like any
 * announcement item. The *where the preset mp3 lives* is surface-specific
 * (web bundles them under its Vite base path; the native shells fetch the
 * same files from the app origin and cache them like any media) — so preset
 * URL resolution is injected via `resolveToneUrlWith`. Everything else is
 * pure and shared, which is what makes the tone behave identically on web,
 * desktop, TV, and mobile.
 *
 * Two kinds of tone:
 *  - presets ("ding" | "chime" | "bell") — shipped with the player;
 *  - `asset:<mediaAssetId>` — a workspace audio upload, hydrated server-side
 *    into `DeviceInfo.announcementToneUrl` (presigned, refreshed every sync).
 *    A stale/dead hydrated URL must never block the announcement: the tone's
 *    media error is swallowed by the player (no failure-budget slot) and
 *    playback advances straight to the announcement.
 */

export const ANNOUNCEMENT_TONES = ["ding", "chime", "bell"] as const;
export type AnnouncementTone = (typeof ANNOUNCEMENT_TONES)[number];

export const ASSET_TONE_PREFIX = "asset:";
export type AssetToneRef = `${typeof ASSET_TONE_PREFIX}${string}`;
export type AnnouncementToneSetting = AnnouncementTone | AssetToneRef;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isAssetToneRef(value: unknown): value is AssetToneRef {
  return (
    typeof value === "string" &&
    value.startsWith(ASSET_TONE_PREFIX) &&
    UUID_RE.test(value.slice(ASSET_TONE_PREFIX.length))
  );
}

/** The mediaAssetId inside an `asset:<id>` tone ref, or null otherwise. */
export function assetToneMediaAssetId(tone: unknown): string | null {
  return isAssetToneRef(tone) ? tone.slice(ASSET_TONE_PREFIX.length) : null;
}

const TONE_ID_PREFIX = "tone:";

/** Approximate encoded durations of the shipped tone assets (seconds). */
const TONE_DURATION_SEC: Record<AnnouncementTone, number> = {
  ding: 1,
  chime: 1.2,
  bell: 1.4,
};

export function isPresetTone(value: unknown): value is AnnouncementTone {
  return ANNOUNCEMENT_TONES.includes(value as AnnouncementTone);
}

/** Allowlist read of settings.announcementTone; anything else means off. */
export function readAnnouncementTone(
  settings: unknown,
): AnnouncementToneSetting | null {
  const raw = (settings as { announcementTone?: unknown } | null | undefined)
    ?.announcementTone;
  if (isPresetTone(raw)) return raw;
  if (isAssetToneRef(raw)) return raw;
  return null;
}

/**
 * The playable URL for the configured tone, or null when no tone should play.
 * Presets resolve through the surface-injected `presetUrl` resolver (web:
 * bundled asset path; native: app-origin URL); asset tones require the
 * server-hydrated presigned URL (DeviceInfo.announcementToneUrl) — when it is
 * missing (asset deleted, uploads unconfigured, pre-first-sync) the tone is
 * skipped and the announcement plays without it.
 */
export function resolveToneUrlWith(
  settings: unknown,
  hydratedUrl: string | null | undefined,
  presetUrl: (tone: AnnouncementTone) => string | null,
): string | null {
  const tone = readAnnouncementTone(settings);
  if (!tone) return null;
  if (isPresetTone(tone)) return presetUrl(tone);
  return hydratedUrl ?? null;
}

export function isToneItemId(id: string | null | undefined): boolean {
  return typeof id === "string" && id.startsWith(TONE_ID_PREFIX);
}

/** Synthetic playable item shaped like the announcement queue's media items. */
export function toneItemFor(
  tone: AnnouncementToneSetting,
  url: string,
): DeviceMediaItem {
  return {
    id: `${TONE_ID_PREFIX}${tone}`,
    source: "asset",
    type: "audio",
    title: t("player.attentionTone"),
    url,
    durationSec: isPresetTone(tone) ? TONE_DURATION_SEC[tone] : null,
  };
}

/** Interrupt-queue entry for the tone, played through the normal playEntry funnel. */
export function toneQueueEntry(
  tone: AnnouncementToneSetting,
  url: string,
): QueueEntry {
  return {
    item: toneItemFor(tone, url),
    reason: "announcement",
    channelType: "announcements",
    playlistId: null,
    playlistName: null,
    scheduleName: null,
    label: t("player.attentionTone"),
    interrupt: true,
  };
}

/**
 * Whether an announcement takeover should be prefixed with the tone. Only one
 * tone per takeover: skip when the on-air item or the head of the interrupt
 * queue is already a tone (e.g. a resync refilled the queue mid-block).
 */
export function shouldPrependTone(
  tone: AnnouncementToneSetting | null,
  currentItemId: string | null | undefined,
  queueHeadItemId: string | null | undefined,
): boolean {
  if (!tone) return false;
  if (isToneItemId(currentItemId)) return false;
  if (isToneItemId(queueHeadItemId)) return false;
  return true;
}
